import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnButton from "@shared/components/SnButton";
import SnTab from "@shared/components/SnTab";
import SnRadioBox from "@shared/components/SnRadioBox";
import { global } from "@shared/libs/Global";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import {
  ColumnConfig,
  SnGridRef,
} from "@shared/components/GridV2/SnGridTypes";
import CommonSpinner from "@widgets/CommonSpinner";
import { createPivotGridData } from "./pivotUtils";
interface Props {
  Menuprops: menuInfo;
}

type PivotViewType = "MH" | "RATE";

const SN_PMS_PJT_REG_022 = forwardRef((_: Props, ref) => {
  const { userInfo } = global();
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const searchRef = useRef<any>(null);
  // 조회할 때마다 프로젝트 컬럼이 바뀌므로 초기 grid3 설정을 별도로 보관한다.
  const grid3BaseColumnsRef = useRef<ColumnConfig[]>([]);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColumns] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [pivotViewType, setPivotViewType] = useState<PivotViewType>("MH");

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  // 검색 조건과 Pivot 표시 방식(MH/비율)을 함께 전달해 화면 데이터를 조회한다.
  const fetchData = (viewType: PivotViewType) =>
    fnGetDataSet({
      id: "sn_pms_pjt_reg_022_01",
      params: {
        ...searchRef.current.getFormdata(),
        tp_view: viewType,
      },
    });

  // 세 번째 조회 결과를 Pivot 구조로 변환하고 필요한 Grid 데이터만 갱신한다.
  const applyData = (data: any, pivotOnly = false) => {
    const pivotGrid = createPivotGridData(
      data[2] || [],
      grid3BaseColumnsRef.current
    );

    setColumns((prev: any) => ({
      ...prev,
      grid3: pivotGrid.columns,
    }));
    setDataSources((prev: any) =>
      pivotOnly
        ? { ...prev, grid3: pivotGrid.rows }
        : {
            grid1: data[0],
            grid2: data[1] || [],
            grid3: pivotGrid.rows,
          }
    );
  };

  const onPivotViewChanged = async (value: string) => {
    const viewType = value as PivotViewType;
    setPivotViewType(viewType);

    try {
      setLoading(true);
      // MH/비율 변경 시 전체 화면 데이터는 유지하고 세 번째 조회 결과만 갱신한다.
      const data: any = await fetchData(viewType);

      // 동적으로 만든 프로젝트 컬럼과 사원별 Pivot 행을 grid3에 반영한다.
      getGridCurrent("grid3").resetUpdate();
      applyData(data, true);
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onSave,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData({
          ...rtn.formData,
          cd_emp: "",
        });
        setColumns({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
        // 동적 컬럼을 다시 만들 때 사용할 grid3의 원본 메타데이터
        grid3BaseColumnsRef.current = rtn.grid.grid3;
      } catch (error) {}
    };
    fetchform();
  }, []);

  const onSearch = async (forceRefresh: boolean = false) => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    if (!forceRefresh && modifiedRowsH.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();

      const data: any = await fetchData(pivotViewType);
      // 세 번째 조회 결과를 사원별 행, 프로젝트별 열 구조로 변환한다.
      applyData(data);
    } catch (error: any) {}
  };

  const onInsert = () => {
    try {
      getGridCurrent("grid1").resetUpdate();
      setDataSources({
        grid1: [],
      });
    } catch (error: any) {}
  };

  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();

      // H 필수값 체크
      const requiredValuesCheck = getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheck) {
        return false;
      }

      const worklaborChanges = getGridCurrent("grid1").getChange();

      if (worklaborChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 요청 데이터 구성
      const SaveData = {
        worklabor: worklaborChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_PMS_PJT_REG_022_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const visibleIdx = focusedRowNodeH.rowIndex || 0;
      getGridCurrent("grid1").clearFocus();
      getGridCurrent("grid1").selectAndFocusRow(visibleIdx);

      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      return false;
    }
  };

  const onAddRow = () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();

      return gridRefs.current["grid1"].current?.addRow({});
    } catch (error: any) {}
  };

  const onDeleteRow = () => {
    gridRefs.current["grid1"].current?.deleteSelectedRows();
  };

  const onBeforeAddRowH = (): any => {
    try {
      const maxNoLine = getGridCurrent("grid1").getMaxValue("no_line");

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        no_line: maxNoLine + 1,
        no_wbs: "none",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onGrid1BtnsClick = (id: string) => {
    if (id === "addRow") return onAddRow();
    if (id === "deleteRow") return onDeleteRow();
    if (id === "btnSync") return onSync();
  };

  const onSync = async () => {
    const currentFormData = searchRef.current?.getFormdata?.() || {};
    const searchData = {
      ...currentFormData,
      cd_firm: currentFormData.cd_firm || userInfo?.cd_firm,
    };

    const syncParam = {
      ...searchData,
      dt_from: searchData.dt_from ?? "",
      dt_to: searchData.dt_to ?? "",
    };
    const confirm = await showDialog(
      "confirm",
      "해당 일자의 실적 데이터를 받아오시겠습니까? 기존 데이터는 삭제됩니다."
    );
    if (!confirm) return;

    try {
      setLoading(true);
      const ok = await fnSave("SN_PMS_PJT_REG_022_Sync", syncParam, undefined, {
        silentSuccess: true,
      });
      if (!ok) return;

      await showDialog("success", "실적 연동이 완료되었습니다.");
      await onSearch(true);
    } finally {
      setLoading(false);
    }
  };

  const onFocusedRowChanged = (event: any) => {
    if (event) setSelectedDocu(event.cd_pjt);
  };

  const grids = {
    grid1: {
      columns: columns.grid1,
      dataSource: dataSources.grid1,
      events: {
        onFocusedRowChanged,
        onBtnAddClick: onGrid1BtnsClick,
        onBtnDeleteClick: onGrid1BtnsClick,
        onBeforeAddRow: onBeforeAddRowH,
      },
    },
    grid2: {
      columns: columns.grid2,
      dataSource: dataSources.grid2,
    },
    grid3: {
      columns: columns.grid3,
      dataSource: dataSources.grid3,
    },
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="94.5%">
          <CommonSpinner isLoading={loading} />
          <SnTab
            titles={["생산공수실적", "대일정", "Pivot"]}
            width={"100%"}
            header={[
              <SnTabHeader
                onBtnAddClick={onGrid1BtnsClick}
                onBtnDeleteClick={onGrid1BtnsClick}
                body={[
                  <div>
                    <SnButton
                      text={"실적연동"}
                      onClick={() => {
                        onGrid1BtnsClick("btnSync");
                      }}
                    />
                  </div>,
                ]}
              />,
              <SnTabHeader />,
              <SnTabHeader
                body={[
                  <div>
                    <SnRadioBox
                      defaultValue={pivotViewType}
                      sql={`
                        select 'MH' as code, 'MH' as name
                        union all
                        select 'RATE' as code, '비율' as name
                      `}
                      onValueChanged={onPivotViewChanged}
                    />
                  </div>,
                ]}
              />,
            ]}
            body={[
              <div className="h-full">
                <SnGrid
                  ref={gridRefs.current["grid1"]}
                  columnConfig={grids.grid1.columns}
                  rowData={grids.grid1.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                  }}
                  events={grids.grid1.events}
                />
              </div>,
              <div className="h-full">
                <SnGrid
                  ref={gridRefs.current["grid2"]}
                  columnConfig={grids.grid2.columns}
                  rowData={grids.grid2.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                  }}
                />
              </div>,
              <div className="h-full">
                <SnGrid
                  ref={gridRefs.current["grid3"]}
                  columnConfig={grids.grid3.columns}
                  rowData={grids.grid3.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                    isEditable: false,
                  }}
                />
              </div>,
            ]}
          />
        </SnResizeBar>
        <SnFileUploadBox
          selectedDocu={selectedDocu}
          selectedLineDocu={0}
        />
      </div>
    </div>
  );
});

export default SN_PMS_PJT_REG_022;
